import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { WaitlistCTA } from '../components/WaitlistCTA';
import { Plus, Search, SlidersHorizontal, ArrowUp, Monitor, Smartphone } from 'lucide-react';
import { DottedLogo } from '../components/DottedLogo';
import { StartingPointSelector } from '../components/StartingPointSelector';
import { UseCaseCrossLinks } from '../components/UseCaseCrossLinks';
import { UseCaseTestimonials } from '../components/UseCaseTestimonials';
import { ClosingCTA } from '../components/ClosingCTA';
const TESTIMONIALS = [{
  name: 'Sarah Chen',
  role: 'CEO & Founder',
  avatar: 'https://i.pravatar.cc/100?img=47',
  quote: 'I used to spend Sunday nights digging through Slack and email to prep for Monday. Now Giga drops a briefing doc in my lap at 8 AM.'
}, {
  name: 'Marcus Weaver',
  role: '2x Founder',
  avatar: 'https://i.pravatar.cc/100?img=11',
  quote: 'The open loop sweep is a lifesaver. It caught an investor request I completely missed in a WhatsApp thread.'
}, {
  name: 'Elena Rostova',
  role: 'Chief of Staff',
  avatar: 'https://i.pravatar.cc/100?img=32',
  quote: "It's like having a co-pilot who has read every email, CRM note, and Slack message we've ever received."
}];
const ROTATING_CLAIMS = ['Runs your morning briefing', 'Triages your inbox', 'Books your travel', 'Reminds you to follow-up', 'Reschedules your week', 'Researches your competitors', 'Runs your errands', 'Preps you for interviews'];
const FEATURED_APPS = [{
  slug: 'hubspot',
  label: 'HubSpot'
}, {
  slug: 'salesforce',
  label: 'Salesforce'
}, {
  slug: 'slack',
  label: 'Slack'
}, {
  slug: 'gmail',
  label: 'Gmail'
}, {
  slug: 'google-calendar',
  label: 'Calendar'
}, {
  slug: 'whatsapp',
  label: 'WhatsApp'
}, {
  slug: 'amazon',
  label: 'Amazon'
}];
type GalleryCard = {
  id: string;
  kind: string;
  title: string;
  quote: string;
  gradient: string;
  icon: string;
  ui: React.ReactNode;
};
// Cards intentionally alternate Business → Personal → Business → … so the
// marquee oscillates between the two worlds as it scrolls.
const GALLERY_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  kind: 'Business',
  title: 'Sales calls, prepped',
  quote: '"Prep me for my Acme call tomorrow. Fetch the HubSpot deal, search Gmail for recent threads, and summarize the proposal."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9949" data-mp="swsuqda78u5z">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-9950" data-mp="nwwnm567gmm7" />
        <div className="flex justify-between items-center mb-2" data-id="element-9951" data-mp="nz9ex8xus9bx">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-9952" data-mp="jx5y04sf0cj9">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-9953" data-mp="2iaxmur5o3bmt">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-9954" data-mp="2eado5j5g3zyk" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-9955" data-mp="2waumltt2h0" />
      </div>
}, {
  id: 'travel-booked',
  kind: 'Personal',
  title: 'Travel, booked',
  quote: '"Book my Friday trip to SF. Find a window seat under $400, a hotel near the office, and add it all to my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'skyscanner',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9956" data-mp="dspzb3tf5kq7">
        <div className="flex items-center justify-between mb-3" data-id="element-9957" data-mp="3dapmmgla8iob">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-9958" data-mp="3i8r7n729qs2f">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-9959" data-mp="3m8jixrj1rcbh">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-9960" data-mp="3r9guvmdgra4j">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-9961" data-mp="11j3vn6j1e5ql" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-9962" data-mp="11ik91cifyt68" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-9963" data-mp="16hngt4pt3dhw" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-9964" data-mp="16jzuh5oj71p5" />
      </div>
}, {
  id: 'board-decks',
  kind: 'Business',
  title: 'Board decks, updated',
  quote: '"Update the board deck with the latest metrics. Pull MRR from Stripe, burn from QuickBooks, and pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9965" data-mp="19z1inbpsg2kq">
        <div className="flex justify-between items-center mb-3" data-id="element-9966" data-mp="1nykkxnvx4jp4">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-9967" data-mp="1j1aovpe0x9sb">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-9968" data-mp="1xpyg5jgu6xcg">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-9969" data-mp="1sq2cxmfyr9xy">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-9970" data-mp="26ptufx5ov8gp" data-mpi={i} />)}
        </div>
      </div>
}, {
  id: 'errands-handled',
  kind: 'Personal',
  title: 'Errands, handled',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Confirm when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9971" data-mp="2cbce7s548euw">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-4" data-id="element-9972" data-mp="2gco1vs4fiq0g" />
        <div className="space-y-2.5" data-id="element-9973" data-mp="22m2je4iw0i33">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-9974" data-mp="22kriccgttofo" data-mpi={i}>
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-9975" data-mp="27evo4secvqf6" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-9976" data-mp="1smbo40mi5z1y" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-triaged',
  kind: 'Business',
  title: 'Inbox, triaged',
  quote: '"Clear my inbox. Reply to anything routine, flag what needs me, and turn the rest into tasks so nothing slips."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9977" data-mp="3qcqvxbq9mxsa">
        <div className="flex items-center justify-between mb-3" data-id="element-9978" data-mp="3ljpcqrj10c2w">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-9979" data-mp="3glnuu9xc6it0">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-9980" data-mp="3ckkhpv6ob0vh">3 left</span>
        </div>
        <div className="space-y-2" data-id="element-9981" data-mp="3cir94aci26ku">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-9982" data-mp="d45q4bcv1rwt">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-9983" data-mp="85udxtz3f5bf" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-9984" data-mp="44qxp6d4v79o" />
            </div>)}
        </div>
      </div>
}, {
  id: 'track-open-loops',
  kind: 'Personal',
  title: 'Open loops, closed',
  quote: '"Show me every open loop across work and life. Check my work email, personal email, Slack, and iMessage for anything I missed."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-9985" data-mp="2jy9eecromluz">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-9986" data-mp="14cysc5l26o89" />
        <div className="space-y-2.5" data-id="element-9987" data-mp="14dgu1hqhpunk">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-9988" data-mp="18e0sqpqpp6bt">
              <div className="w-3 h-3 rounded-sm border border-neutral-300 shrink-0" data-id="element-9989" data-mp="18cppcr16i0l8" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-9990" data-mp="1d6k9twolyjld" />
            </div>)}
        </div>
      </div>
}];
function GeneratingStep({
  icon,
  text,
  index
}: {
  icon: string;
  text: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <div className="flex items-start gap-3" data-id="element-9991" data-mp="1s3b63x2zjf6r">
      <div className="relative w-6 h-6 shrink-0 mt-0.5" data-id="element-9992" data-mp="1w4n1ykcgcwne">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: reduce ? 0 : index * 0.25
      }} className="absolute inset-0 rounded bg-white/10 flex items-center justify-center z-10" data-id="element-9993" data-mp="1w20u0jlngf4n">
          <AppIcon slug={icon} className="w-3.5 h-3.5 object-contain" data-id="element-9994" data-mp="3nrbckjp66a2c" />
        </motion.div>
        {!reduce && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} whileInView={{
        opacity: [0, 1, 0],
        scale: [0.8, 1.4, 1.6]
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        delay: index * 0.25,
        ease: 'easeOut'
      }} className="absolute inset-0 rounded border border-[#EA580C]/40" data-id="element-9995" data-mp="3jq7ya1mrsm38" />}
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed flex flex-wrap gap-x-[0.25em]" data-id="element-9996" data-mp="301h1a6ki0tk7">
        {words.map((word, wIdx) => <motion.span key={wIdx} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : index * 0.25 + 0.1 + wIdx * 0.02
      }} className="inline-block" data-id="element-9997" data-mp="k9t5wfl4v93i" data-mpi={wIdx}>
            {word}
          </motion.span>)}
      </p>
    </div>;
}
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-9998" data-mp="iuqghjimd3p1" />;
}
function GalleryJobCard({
  card
}: {
  card: GalleryCard;
}) {
  const isBusiness = card.kind === 'Business';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-9999" data-mp="1ph2oksgzxaaa">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl bg-gradient-to-br ${card.gradient} p-6 relative overflow-hidden flex flex-col items-center justify-center`} data-id="element-10000" data-mp="1u9m4vx72ijis">
        <div className="relative z-10 flex flex-col items-center" data-id="element-10001" data-mp="1y2fcyrkart4z">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-10002" data-mp="1y01l0rkwyx1n" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center" data-id="element-10003" data-mp="1k1uuxxm7i1cj">
            <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-10004" data-mp="1p3ka9pggp16f" />
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-10005" data-mp="1f3jlhnztpsuu" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-10006" data-mp="15cyrkdv3hci9">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-10007" data-mp="15bnokg9h48s7">
          <span className={`w-1.5 h-1.5 rounded-full ${isBusiness ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-10008" data-mp="rg1zya75jr0b" />
          <span className={isBusiness ? 'text-[#EA580C]' : 'text-neutral-500'} data-id="element-10009" data-mp="wewjjecy1jlh">
            {card.kind}
          </span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-10010" data-mp="7sp6bdtdkgvh">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-10011" data-mp="mp6ddsuj79c8">{card.quote}</p>
      </div>
    </div>;
}
export function FoundersExecs() {
  const [claimIndex, setClaimIndex] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setClaimIndex(i => (i + 1) % ROTATING_CLAIMS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [reduce]);
  return <>
      <section className="relative w-full bg-[#FAF8F4] text-[#2B211A] overflow-hidden" data-id="element-10012" data-mp="15emh0p1ijhwv">
        {/* Cream dot-grid texture */}
        <div aria-hidden="true" className="cream-grid z-0" data-id="element-10013" data-mp="10ixmx189it7y" />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 md:pt-40 pb-20 md:pb-28" data-id="element-10014" data-mp="1ehnkehyzuuwu">
          {/* Eyebrow */}
          <div className="text-center" data-id="element-10015" data-mp="1agvbxqxy4u1m">
            <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-5" data-id="element-10016" data-mp="1hih0ksam7vd6">
              Founders & Execs
            </span>

            {/* Headline */}
            <h1 className="font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2B211A]" data-id="element-10017" data-mp="1rgoet3dirzbh">
              Your new assistant that
            </h1>

            {/* Rotating claim */}
            <div className="mt-4 h-[1.2em] font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#EA580C]" data-id="element-10018" data-mp="207oozpy04mjg">
              <AnimatePresence mode="wait" data-id="element-10019" data-mp="2a6wbp3qmltj8">
                <motion.span key={claimIndex} initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -14
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="block" data-id="element-10020" data-mp="2a7xl73rzu7yu">
                  {ROTATING_CLAIMS[claimIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4" data-id="element-10021" data-mp="2t9ogzvt8k2dw">
              <WaitlistCTA className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-10022" data-mp="2pa5tqtdxzf7w">
                Get Started
              </WaitlistCTA>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400" data-id="element-10023" data-mp="3cyyqut6avt43">
                Each Giga plan has a 7 day free trial.
              </p>
            </div>

            {/* Featured apps */}
            <div className="mt-16 pt-10 border-t border-[#EAE4D9] max-w-5xl mx-auto" data-id="element-10024" data-mp="330j0isf3knvn">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-400 mb-6" data-id="element-10025" data-mp="1wm9zrwfrc1et">
                Featured Apps
              </p>
              <div className="flex flex-wrap md:flex-nowrap justify-center gap-3 md:gap-4" data-id="element-10026" data-mp="1roi8n4mxsjbo">
                {FEATURED_APPS.map(app => <div key={app.label} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EAE4D9] px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow" data-id="element-10027" data-mp="3k9blfyc9vxz7">
                    <AppIcon slug={app.slug} className="w-5 h-5 object-contain grayscale opacity-70" data-id="element-10028" data-mp="36gc6bxgek5s6" />
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-10029" data-mp="3bevt202tllll">
                      {app.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Put Giga to work — job gallery */}
          <div className="mt-24 md:mt-32 w-full overflow-hidden" data-id="element-10030" data-mp="3p7v7dyqv99rk">
            <div className="text-center mb-12 max-w-2xl mx-auto" data-id="element-10031" data-mp="3u786bhv0i7ai">
              <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-4" data-id="element-10032" data-mp="3u9u9z6es82ng">
                Put Giga to work
              </span>
              <h2 className="font-title font-bold text-3xl md:text-4xl text-[#2B211A]" data-id="element-10033" data-mp="2iu5qwq4nfnrx">
                Give Giga a job.
              </h2>
              <p className="mt-4 text-base md:text-lg text-neutral-500 leading-relaxed" data-id="element-10034" data-mp="269z0woqs733h">
                One assistant for both worlds — it moves between your business
                and your personal life without missing a beat.
              </p>
            </div>

            {/* Auto-scrolling marquee, oscillating Business ↔ Personal.
             Falls back to a manual horizontal scroll for reduced motion. */}
            {reduce ? <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-10035" data-mp="1nka8p3ebe0rt">
                {GALLERY_CARDS.map(card => <GalleryJobCard key={card.id} card={card} data-id="element-10036" data-mp="21c6zn448y4mm" />)}
              </div> : <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-10037" data-mp="1wj49l5yohdb6">
                <motion.div className="flex gap-6 w-max" animate={{
              x: ['-50%', '0%']
            }} transition={{
              duration: 48,
              ease: 'linear',
              repeat: Infinity
            }} data-id="element-10038" data-mp="18s8dhwn6vvy4">
                  {[...GALLERY_CARDS, ...GALLERY_CARDS].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} data-id="element-10039" data-mp="38urdv8z307na" data-mpi={i} />)}
                </motion.div>
              </div>}
          </div>

          {/* Giga Does the Work comparison section */}
          <div className="mt-32 md:mt-40" data-id="element-10040" data-mp="3ioqe22flhhcl">
            <div className="text-center mb-20" data-id="element-10041" data-mp="2kzex37vo9myh">
              <span className="inline-block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4" data-id="element-10042" data-mp="2l0pzp4ddsoo0">
                Giga does the work
              </span>
              <h2 className="font-title font-bold text-4xl md:text-5xl text-[#2B211A]" data-id="element-10043" data-mp="2uw023t7oyy2b">
                Other tools advise.{' '}
                <span className="text-[#EA580C]" data-id="element-10044" data-mp="2yzzeq0pupcqe">Giga executes.</span>
              </h2>
            </div>

            <div className="space-y-24" data-id="element-10045" data-mp="a8wvzj5m7xtf">
              {/* Row 1: MEETING PREP */}
              <div className="flex flex-col gap-8" data-id="element-10046" data-mp="e6m92wb37imo">
                <div className="text-center max-w-3xl mx-auto" data-id="element-10047" data-mp="1yqzq1v3z863n">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-10048" data-mp="1ysauzy95rjwc">
                    Meeting Prep
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-10049" data-mp="1pwldw1eqtbor">
                    Prep me for my Acme call tomorrow
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-10050" data-mp="1g1jmt00wpzjj">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-10051" data-mp="1b38favy3414h">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-10052" data-mp="120h2osth2zb9">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-10053" data-mp="31d1kr6c8ek00">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-10054" data-mp="258w8du680qca">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-10055" data-mp="2a9sbi5ir08gn">
                      "Log into HubSpot, read the notes. Log into Gmail, search
                      the thread. Open Google Drive, find the proposal. Spend 30
                      minutes piecing it together..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-10056" data-mp="1ck745m9vc4wb">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-10057" data-mp="1hk36zi6txqaa" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-10058" data-mp="36z9jir57e6li">
                      <div className="flex items-center gap-2" data-id="element-10059" data-mp="3bwszbvhjurfk">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-10060" data-mp="109g2dtdendxw" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10061" data-mp="1577rygqkunyx" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-10062" data-mp="1swkba9fmccey">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-10063" data-mp="1o0v5hod09za7" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-10064" data-mp="3us47dsp96ycb" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-10065" data-mp="1mpt1it3lhwqk">
                      {[{
                      icon: 'hubspot',
                      text: 'Fetching the Acme deal record and stage'
                    }, {
                      icon: 'gmail',
                      text: 'Searching recent threads for context'
                    }, {
                      icon: 'google-drive',
                      text: 'Finding the latest proposal draft'
                    }, {
                      icon: 'slack',
                      text: 'Drafting the briefing doc for the team'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-10066" data-mp="1drr3mmf5rk7i" data-mpi={i} />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-10067" data-mp="18t64kqbn76kn">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-10068" data-mp="3agiqdts2na97">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-10069" data-mp="31o6zk6t1pksp">
                        You walk into the meeting with the exact context you
                        need, zero digging required.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Row 2: OPEN LOOPS */}
              <div className="flex flex-col gap-8" data-id="element-10070" data-mp="22a1avord1ls">
                <div className="text-center max-w-3xl mx-auto" data-id="element-10071" data-mp="bv4m68i9hpzy">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-10072" data-mp="7v3yryd6q4gr">
                    Open Loops
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-10073" data-mp="2xu70c2bpgpr">
                    What am I forgetting across work and life?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-10074" data-mp="3kc95ajuay36j">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-10075" data-mp="3g9un81ygj7gb">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-10076" data-mp="3bcm684p1p9gn">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-10077" data-mp="36bexa2b6248e">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-10078" data-mp="2r8etj2zj919l">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-10079" data-mp="317crewium759">
                      "You can't. You just have to remember everything, or spend
                      2 hours manually sweeping 4 inboxes and 3 chat apps."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-10080" data-mp="2dfxlktrv57p9">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-10081" data-mp="2n83a1tiwp6zp" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-10082" data-mp="1psz7u3v4odv1">
                      <div className="flex items-center gap-2" data-id="element-10083" data-mp="1uqqzqxvrvzz8">
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-10084" data-mp="bbqwvh83wbz" />
                        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-10085" data-mp="593redvxoqrs" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-10086" data-mp="syo9kuk9jk00">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-10087" data-mp="o0mvko1p2ave" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-10088" data-mp="2urw0l198nq1q" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-10089" data-mp="159p3ohp8uu7a">
                      {[{
                      icon: 'gmail',
                      text: 'Scanning Work Email for promised follow-ups'
                    }, {
                      icon: 'google-calendar',
                      text: 'Checking Personal Calendar for family obligations'
                    }, {
                      icon: 'slack',
                      text: 'Searching Slack DMs and exec channels'
                    }, {
                      icon: 'apple',
                      text: 'Finding loose texts and quick asks'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-10090" data-mp="cn4bjf0y2e2o" data-mpi={i} />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-10091" data-mp="hmqqbswf82dl">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-10092" data-mp="2t7zw8dnrmbce">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-10093" data-mp="2ji6jh9bvzq6k">
                        You never drop a ball again. Every open loop, surfaced
                        and prioritized.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pick the function that eats the most time, then see Giga handle it */}
      <StartingPointSelector data-id="element-10094" data-mp="tr7oa2uxdyqs" />

      {/* Role-relevant social proof right before the conversion moment */}
      <UseCaseTestimonials testimonials={TESTIMONIALS} data-id="element-10095" data-mp="yq2f9s31qtlz" />

      {/* Keep visitors in the funnel: other use cases, then the conversion CTA */}
      <UseCaseCrossLinks current="founders-execs" data-id="element-10096" data-mp="17p5x6sqh92yw" />
      <ClosingCTA data-id="element-10097" data-mp="17mtak29ry4fi" />
    </>;
}